/**
 * Pearls — the breadth currency (doc 08 §Earning).
 *
 * Every clear pays on the spot, which is what makes "failure always pays" true:
 * a run that dies six fights in has already banked six paydays. The itemised
 * lines come back with the award so the payday banner can show its own maths.
 */

import { PEARL_RATES } from './tree.ts';

export interface PearlLine {
  label: string;
  pearls: number;
}

export interface Award {
  pearls: number;
  lines: PearlLine[];
  /** Species that just entered the Codex (already counted in `pearls`). */
  newCodex: string[];
}

export interface ClearInput {
  depth: number;
  shoalName: string;
  riled: boolean;
  boss?: 'miniboss' | 'boss';
  /** Shoals still circling when this boss was pulled — doc 14's early-pull greed. */
  skips: number;
  /** Species killed here that the profile had never seen. */
  newSpecies: string[];
  /** First time this shoal has ever been cleared on this depth. */
  firstClear: boolean;
  pressureTiers?: number;
}

/** One cleared shoal's Pearls, itemised. Fractions round once, at the end. */
export function clearAward(input: ClearInput): Award {
  const r = PEARL_RATES;
  const lines: PearlLine[] = [];

  let subtotal = r.shoalCleared * input.depth;
  lines.push({ label: 'SHOAL CLEARED', pearls: subtotal });

  if (input.boss) {
    const base = (input.boss === 'boss' ? r.bossKilled : r.minibossKilled) * input.depth;
    const skipBonus = Math.min(r.bossSkipBonusMax, input.skips * r.bossSkipBonus);
    const bossPearls = base * (1 + skipBonus);
    lines.push({
      label: input.boss === 'boss' ? 'BOSS KILLED' : 'MINI-BOSS KILLED',
      pearls: bossPearls,
    });
    if (skipBonus > 0) {
      lines.push({
        label: `PULLED EARLY · ${input.skips} SHOAL${input.skips === 1 ? '' : 'S'} SKIPPED +${Math.round(skipBonus * 100)}%`,
        pearls: 0,
      });
    }
    subtotal += bossPearls;
  }

  if (input.riled) {
    const bonus = subtotal * r.riledBonus;
    lines.push({ label: `RILED +${Math.round(r.riledBonus * 100)}%`, pearls: bonus });
    subtotal += bonus;
  }

  if (input.firstClear) {
    const bonus = subtotal * (r.firstClearMult - 1);
    lines.push({ label: `FIRST TIME EVER ×${r.firstClearMult}`, pearls: bonus });
    subtotal += bonus;
  }

  if (input.newSpecies.length > 0) {
    const codex = r.codexFirstKill * input.newSpecies.length;
    lines.push({ label: `CODEX · ${input.newSpecies.length} NEW SPECIES`, pearls: codex });
    subtotal += codex;
  }

  const tiers = input.pressureTiers ?? 0;
  if (tiers > 0) {
    const bonus = subtotal * (r.pressureTierBonus * tiers);
    lines.push({ label: `PRESSURE ×${tiers} +${Math.round(r.pressureTierBonus * tiers * 100)}%`, pearls: bonus });
    subtotal += bonus;
  }

  return {
    pearls: Math.round(subtotal),
    lines: lines.map((l) => ({ label: l.label, pearls: Math.round(l.pearls) })),
    newCodex: input.newSpecies,
  };
}
